/** @jsx h */
import { h, Component, Fragment } from 'preact'
import CoverSlide from './CoverView'
import CreditsSlide from './CreditsView'
import Slide from './Slide'
import Header from './Header'
import Navigation from './Navigation'
import Drawer from './Drawer'
import EvidenceCard from '../ModalCard/EvidenceCard'
import CertificateCard from '../ModalCard/CertificateCard'
import AboutCard from '../ModalCard/about/AboutCard'
import Progress from './Progress'
import ProgressBar from './ProgressBar'
import * as analytics from '../../../utils/analytics'
import { getMousePos } from '../../utils/mouse'
import { onResize, onScroll } from './callbacks'

export default class Slider extends Component {
  state = { index: 0 }

  componentDidMount () {
    document.addEventListener('mouseup', this.onMouseUp)
    window.addEventListener('resize', onResize)
    this.__P.addEventListener('scroll', onScroll)

    analytics.track('productStoryPageViewed', {
      contentType: 'ProductStory',
      contentId: this.props.id,
      index: this.state.index
    })
    analytics.track('productStoryPage_viewed', {
      object: 'productStoryPage',
      action: 'viewed',
      content_type: 'ProductStory',
      content_id: this.props.id,
      page_index: this.state.index,
      stages_completed: this.state.index === this.props.stages.length
    }, { adapter: 'segment' })
  }

  componentWillUnmount () {
    document.removeEventListener('mouseup', this.onMouseUp)
    window.removeEventListener('resize', onResize)
    this.__P.removeEventListener('scroll', onScroll)
    onScroll(false) // invalidates memoization and allows reuse in another Slider instance
  }

  onMouseUp = event => {
    if (!event.target.classList.contains('Journey-content') &&
        !event.target.classList.contains('Journey-backgroundVideo--content')) return

    if (this.state.helpTipOpen) {
      this.toggleHelpTip()
      return
    }

    const { x } = getMousePos(this.__P, event)

    if (x > this.__P.clientWidth / 2) {
      this.next()
      event.target.parentNode.nextElementSibling?.scrollIntoView()
    } else {
      this.previous()
      event.target.parentNode.previousElementSibling?.scrollIntoView()
    }
  }

  closeDrawer = e => {
    this.setState({ modalOpen: false })

    setTimeout(() => {
      this.setState({ modalContent: null })
    }, Drawer.ANIMATION_DURATION)
  }

  populateDrawer = modalContent => {
    this.setState({ modalOpen: true, modalContent })
  }

  toggleHelpTip = e => {
    this.setState(prevState => ({ helpTipOpen: !prevState.helpTipOpen }))
  }

  previous = () => {
    if (this.state.index <= 0) return

    this.setState(prevState => ({ index: prevState.index - 1 }))

    if (this.state.modalOpen) this.closeDrawer()

    this.__P.scrollBy({ left: -1 })
  };

  next = () => {
    if (this.state.index > this.props.stages.length) return

    this.setState(prevState => ({ index: prevState.index + 1 }))

    if (this.state.modalOpen) this.closeDrawer()

    const left = this.__P.offsetWidth || 400
    this.__P.scrollBy({ left })

    analytics.track('productStoryPageViewed', {
      contentType: 'ProductStory',
      contentId: this.props.id,
      index: this.state.index
    })
    analytics.track('productStoryPage_viewed', {
      object: 'productStoryPage',
      action: 'viewed',
      content_type: 'ProductStory',
      content_id: this.props.id,
      page_index: this.state.index,
      stages_completed: this.state.index === this.props.stages.length
    }, { adapter: 'segment' })
  };

  render ({
    id,
    transition,
    title,
    description,
    href,
    featuredImageUrl,
    featuredProduct,
    stages,
    organization,
    exitLink,
    exitButtonLabel,
    poweredBy,
    coverSlideDisabled,
    creditsSlideDisabled,
    journeyBrandDisabled,
    helpButtonDisabled,
    socialShareDisabled
  },
  {
    index,
    modalOpen,
    modalContent
  }) {
    const stepsTotal = stages.length + (coverSlideDisabled ? 0 : 1) + (creditsSlideDisabled ? 0 : 1)

    return (
      <Fragment>
        <Drawer open={modalOpen} close={this.closeDrawer}>
          {modalContent && (modalContent.type === 'Claim'
            // if proof point has an explicit issuer use that
            // else the proof point is self issued, use org
            ? modalContent.issuer
              ? <EvidenceCard {...modalContent} inJourney />
              : <EvidenceCard {...modalContent} inJourney issuer={organization} />
            : (modalContent.type === 'Powered'
                ? <AboutCard revealed inJourney {...modalContent} />
                : <CertificateCard {...modalContent} inJourney />))}
        </Drawer>
        <Navigation
          id={id}
          next={this.next}
          previous={this.previous}
          index={index}
          total={stepsTotal}
        />

        <CoverSlide
          id={id}
          title={title}
          backgroundUrl={featuredImageUrl}
          description={description}
          href={href}
          url='/intro'
          disabled={coverSlideDisabled}
        />

        {stages.map((stage, idx) =>
          <Slide
            {...stage}
            index={idx}
            key={stage.id}
            sliderId={id}
            href={href}
            populateDrawer={this.populateDrawer}
          />
        )}

        {!creditsSlideDisabled &&
          <CreditsSlide
            id={id}
            numberOfStages={stages.length}
            href={href}
            title={title}
            organization={organization}
            exitLink={exitLink}
            exitButtonLabel={exitButtonLabel}
            shareImage={featuredImageUrl}
            socialShareDisabled={socialShareDisabled}
            populateDrawer={this.populateDrawer}
            poweredBy={poweredBy}
          />}

        <Header
          organization={organization}
          product={featuredProduct}
          href={href}
          title={title}
          journeyBrandDisabled={journeyBrandDisabled}
          helpButtonDisabled={helpButtonDisabled}
          socialShareDisabled={socialShareDisabled}
          toggleHelpTip={this.toggleHelpTip}
        >
          <ProgressBar />
          <Progress
            count={index}
            total={stepsTotal}
            direction={transition}
          />
        </Header>
      </Fragment>
    )
  }
}
